/**
 * Compact selectable field used by the Schema Compare Setup dialog.
 *
 * When no value is set, renders a Select with an inline left-icon and
 * placeholder. When a value is set (and the user isn't actively editing),
 * collapses to an underlined text affordance — clicking flips it back into
 * the Select. Saves substantial vertical space vs. label-above-input.
 */

import { useState, useEffect, type ReactNode } from 'react';
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ListingLoadingLabel } from '../../common/components/ListingLoadingLabel';
import type { DatabasesListingPhase } from '@/features/database-objects/services/databasesService';

type SchemaCompareCompactFieldProps = {
  /** Inline left icon — should be sized to ~h-3.5 w-3.5. */
  icon: ReactNode;
  /** Placeholder shown when there is no value (e.g. "Select connection…"). */
  placeholder: string;
  /** Currently-selected raw value (id / name) — empty string when unset. */
  value: string;
  /** Display label rendered when collapsed; falls back to value. */
  displayLabel?: ReactNode;
  /** Children supplied to <SelectContent>. */
  children: ReactNode;
  /** Forwarded to onValueChange of the inner Select. */
  onValueChange: (next: string) => void;
  disabled?: boolean;
  /**
   * True while the field's options are being listed. In this dialog only the
   * database field ever loads (the connection list is already in memory), so
   * the spinner is labelled as a database listing.
   */
  isLoading?: boolean;
  /**
   * Soporte C3 — phase of the in-flight database listing, so the spinner can
   * say "Connecting to server…" instead of a bare "Loading…" while the driver
   * is still reaching a cold remote server.
   */
  loadingPhase?: DatabasesListingPhase;
  /** test id forwarded to the SelectTrigger. */
  testId?: string;
};

export function SchemaCompareCompactField({
  icon,
  placeholder,
  value,
  displayLabel,
  children,
  onValueChange,
  disabled = false,
  isLoading = false,
  loadingPhase,
  testId,
}: SchemaCompareCompactFieldProps) {
  const [editing, setEditing] = useState(!value);

  // When value clears externally (e.g. user changes connection so database
  // resets), reopen the Select so the user can pick again.
  useEffect(() => {
    if (!value) setEditing(true);
  }, [value]);

  const handleChange = (next: string) => {
    onValueChange(next);
    if (next) setEditing(false);
  };

  if (!editing && value) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        disabled={disabled}
        className="flex items-center gap-2 text-sm w-full text-left py-1.5 px-1 rounded hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed group"
        data-testid={testId}
      >
        <span className="text-muted-foreground flex-shrink-0">{icon}</span>
        <span className="font-medium underline decoration-dotted underline-offset-4 decoration-muted-foreground group-hover:decoration-foreground truncate">
          {displayLabel ?? value}
        </span>
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-muted-foreground flex-shrink-0">{icon}</span>
      <Select value={value} onValueChange={handleChange} disabled={disabled}>
        <SelectTrigger className="flex-1 min-w-0" data-testid={testId}>
          {isLoading ? (
            <ListingLoadingLabel target="databases" phase={loadingPhase} />
          ) : (
            <SelectValue placeholder={placeholder} />
          )}
        </SelectTrigger>
        <SelectContent>{children}</SelectContent>
      </Select>
    </div>
  );
}
